// The case for it: why it works, how it compares, the first-film offer, the price.
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import gsap from 'gsap';
import { CRWIcon, type IconName } from '../components/icons';
import { GradBtn, MetalBtn, NeutralTile, SectionHead } from '../components/primitives';
import { CTA_LABEL } from '../lib/site';
import { scrollToHash } from './nav';
import '../styles/compare.css';

const go = (hash: string) => (e: React.MouseEvent) => { e.preventDefault(); scrollToHash(hash); };

/* ---------- WHY IT WORKS: asymmetric bento ---------- */
export function WhyBento() {
  const cell = (extra?: CSSProperties): CSSProperties => ({ borderRadius: 16, padding: 22, display: 'flex', flexDirection: 'column', gap: 9, position: 'relative', overflow: 'hidden', boxSizing: 'border-box', ...extra });
  const big: CSSProperties = { fontFamily: 'var(--font-display)', fontSize: 44, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1, color: 'var(--fg-1)' };
  const cap: CSSProperties = { fontSize: 14.5, fontWeight: 600, color: 'var(--fg-1)' };
  const txt: CSSProperties = { margin: 0, fontSize: 12.5, lineHeight: 1.6, color: 'var(--fg-3)' };
  const stat: CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '0.07em', color: 'var(--fg-4)', textTransform: 'uppercase' };
  const minis: { icon: IconName; t: string; s: string; m: string }[] = [
    { icon: 'film', t: 'Real footage first', s: 'AI scenes are one layer of seven. You approve every still before it moves.', m: 'one layer of seven' },
    { icon: 'monitor', t: 'A project you can open', s: 'Premiere Pro, every clip on its own track. Change anything.', m: 'plus the master file' },
    { icon: 'user', t: 'A person checks it', s: 'Someone approves at a few points. Most of the work runs overnight.', m: 'runs overnight' },
    { icon: 'shield', t: 'The film is yours', s: 'Your channel owns the film and the project.', m: 'yours to keep' },
  ];
  return (
    <section id="why" style={{ padding: '90px 24px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <SectionHead center label="Why it works" title="Built by editors. Run by a system." />
        <div className="crw-bento" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gridTemplateRows: 'auto auto auto', gap: 12 }}>

          {/* YOUR STYLE: hero cell, 2x2 */}
          <div className="crw-glass crw-gradborder" style={cell({ gridColumn: 'span 2', gridRow: 'span 2', padding: 28, justifyContent: 'center' })}>
            <span style={{ position: 'absolute', right: -50, top: -50, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05), transparent 70%)' }}></span>
            <NeutralTile d="refresh" size={44} />
            <span className="crw-h-grad" style={{ ...big, fontSize: 58, marginTop: 6 }}>Your style</span>
            <p style={{ ...txt, fontSize: 14, maxWidth: '40ch' }}>It learns one channel’s taste. Every correction you give is written into its rules for your next film.</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {['your template', 'your fonts', 'your music rules'].map((s) => (
                <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--fg-2)', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--line-1)', padding: '4px 10px', borderRadius: 999 }}>
                  <span style={{ color: 'var(--fg-3)', display: 'inline-flex' }}><CRWIcon d="check" size={9} sw={3} /></span>{s}
                </span>
              ))}
            </div>
          </div>

          {/* HOURS: wide, the race runs itself */}
          <div className="crw-glass crw-gradborder" style={cell({ gridColumn: 'span 2' })}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <NeutralTile d="zap" size={36} />
              <b style={cap}>Hours of editing per film</b>
              <span style={{ flex: 1 }}></span>
              <span className="crw-shimmer" style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 600 }}>~30 h</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ ...stat, width: 86, flex: 'none' }}>Overnight</span>
              <span style={{ flex: 1, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}><i className="crw-bar" style={{ display: 'block', height: '100%', width: '19%', borderRadius: 3, background: 'var(--grad-accent)', transformOrigin: 'left', animationDelay: '400ms', animationDuration: '700ms' }}></i></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ ...stat, width: 86, flex: 'none' }}>By hand</span>
              <span style={{ flex: 1, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}><i className="crw-bar" style={{ display: 'block', height: '100%', width: '100%', borderRadius: 3, background: 'rgba(255,255,255,0.14)', transformOrigin: 'left', animationDelay: '400ms', animationDuration: '3200ms' }}></i></span>
              <span style={stat}>~160 h</span>
            </div>
            <p style={txt}>Our estimate, from our first 50-minute film.</p>
          </div>

          {/* WHO */}
          <div className="crw-glass crw-gradborder" style={cell()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <NeutralTile d="scissors" size={32} />
              <b style={cap}>We edit for a living</b>
            </div>
            <p style={txt}>We cut documentaries for a YouTube channel with 732,000 subscribers.</p>
            <span style={{ ...stat, marginTop: 'auto' }}>732,000 subscribers</span>
          </div>

          {/* TIME */}
          <div className="crw-glass crw-gradborder" style={cell()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <NeutralTile d="layers" size={32} />
              <b style={cap}>Five working days</b>
            </div>
            <p style={txt}>Per film, once we know your style. The first one takes about two weeks.</p>
            <span style={{ ...stat, marginTop: 'auto' }}>voiceover in · film out</span>
          </div>

          {/* minis row */}
          {minis.map((m) => (
            <div key={m.t} className="crw-glass crw-card-hover crw-gradborder" style={cell({ padding: '18px 18px 16px' })}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <NeutralTile d={m.icon} size={32} />
                <b style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--fg-1)' }}>{m.t}</b>
              </div>
              <p style={txt}>{m.s}</p>
              <span style={{ ...stat, marginTop: 'auto' }}>{m.m}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- COMPARE: an alternative picker and one ledger panel ---------- */
type Mark = 'yes' | 'no' | 'part';
// [full text, mark, mobile-short]: the short copy is written for the ~150px
// mobile tile; great compare tables cut the copy, not the columns.
type Cell = [string, Mark, string?];
interface Alt { id: string; name: string; sub: string; icon: IconName; cells: Cell[]; note: string }

const ROWS: { label: string; icon: IconName }[] = [
  { label: 'A finished film', icon: 'film' },
  { label: 'In your channel’s style', icon: 'pen' },
  { label: 'A Premiere project you can edit', icon: 'monitor' },
  { label: 'Footage found for you', icon: 'search' },
  { label: 'Graphics, sound and music', icon: 'sliders' },
  { label: 'Turnaround', icon: 'zap' },
];
const US = ['Yes, every layer', 'Learned, then kept as rules', 'Yes, plus the master file', 'For every sentence', 'Included in every film', 'Five working days'];
const US_M = ['Every layer', 'Learned and kept', 'Yes, plus master', 'Every sentence', 'Included', 'Five working days'];

const ALTS: Alt[] = [
  {
    id: 'editor', name: 'A freelance editor', sub: 'or an editing agency', icon: 'user',
    cells: [
      ['Yes', 'yes'],
      ['After a few films together', 'part', 'After a few films'],
      ['Usually', 'yes'],
      ['By hand, clip by clip', 'part', 'By hand'],
      ['Depends on the editor', 'part', 'Depends'],
      ['As long as the hours take', 'part', 'Their hours'],
    ],
    note: 'A good editor is the standard we hold ourselves to. The limit is hours: one person places every clip by hand.',
  },
  {
    id: 'generator', name: 'AI video generators', sub: 'voiceover to video', icon: 'sparkle',
    cells: [
      ['A video, in one look', 'part', 'One look'],
      ['The same look for everyone', 'no', 'Generic'],
      ['No project file', 'no', 'No project'],
      ['Generated or stock, automatically', 'part', 'Automatic'],
      ['Generic', 'part'],
      ['Minutes', 'yes'],
    ],
    note: 'Fast and cheap. The output looks like every other channel that uses them, and there is no project to open.',
  },
  {
    id: 'assistant', name: 'AI editing assistants', sub: 'rough-cut tools', icon: 'scissors',
    cells: [
      ['A rough cut', 'part'],
      ['They do not learn a channel', 'no', 'Not learned'],
      ['Yes', 'yes'],
      ['Only from footage you already have', 'no', 'Your footage only'],
      ['Still yours to do', 'no', 'Yours to do'],
      ['Fast, for the rough cut', 'part', 'Rough cut only'],
    ],
    note: 'Useful for editors. They cut footage you already have. Finding footage, graphics and sound stays with you.',
  },
];

function MarkIcon({ m, us }: { m: Mark; us?: boolean }) {
  const cls = us ? 'crwc-m crwc-m-us' : m === 'yes' ? 'crwc-m crwc-m-yes' : m === 'part' ? 'crwc-m crwc-m-part' : 'crwc-m crwc-m-no';
  const d = m === 'yes' ? 'check' : m === 'part' ? 'minus' : 'x';
  return <span className={cls}><CRWIcon d={us ? 'check' : d} size={11} sw={2.6} /></span>;
}

export function Compare() {
  const [sel, setSel] = useState(ALTS[0].id);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const prevSel = useRef(sel);
  const target = ALTS.find((c) => c.id === sel)!;

  // switch: outgoing text fades, state swaps, incoming text eases in (below)
  const pick = (id: string) => {
    if (id === sel) return;
    const els = boxRef.current?.querySelectorAll('[data-swap]');
    if (!els?.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setSel(id); return; }
    gsap.to(els, { opacity: 0, y: 4, duration: 0.14, ease: 'power2.in', overwrite: true, onComplete: () => setSel(id) });
  };
  useLayoutEffect(() => {
    if (prevSel.current === sel) return;
    prevSel.current = sel;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const els = boxRef.current?.querySelectorAll('[data-swap]');
    if (els?.length) gsap.fromTo(els, { opacity: 0, y: 4 }, { opacity: 1, y: 0, duration: 0.3, stagger: 0.03, ease: 'power2.out', overwrite: true });
  }, [sel]);

  return (
    <section id="compare" style={{ padding: '90px 24px', position: 'relative' }}>
      <div aria-hidden="true" style={{ position: 'absolute', left: '50%', top: '8%', transform: 'translateX(-50%)', width: 1000, height: 640, background: 'radial-gradient(ellipse, rgba(255,255,255,0.03), transparent 65%)', pointerEvents: 'none' }}></div>
      <div style={{ maxWidth: 920, margin: '0 auto', position: 'relative' }}>
        <SectionHead center label="Overnight vs the alternatives" title="How it compares."
          sub="Three other ways to get a documentary edited. Pick one." />

        {/* alternative picker */}
        <div className="crwc-pick" role="tablist" aria-label="Pick an alternative">
          {ALTS.map((c) => (
            <button key={c.id} className={`crwc-tab${sel === c.id ? ' crwc-tab-on' : ''}`} role="tab" aria-selected={sel === c.id} onClick={() => pick(c.id)}>
              <span className="crwc-tab-mark" style={{ color: 'var(--fg-2)' }}><CRWIcon d={c.icon} size={14} /></span>
              {c.name}
            </button>
          ))}
        </div>

        {/* the ledger */}
        <div className="crwc-panel" ref={boxRef}>
          <div className="crwc-hrow">
            <div className="crwc-hlabel">Head to head</div>
            <div className="crwc-cell crwc-us crwc-hus">
              <img src="/logo-mark.svg" width="14" height="14" alt="" style={{ opacity: 0.95 }} />
              <span>Overnight</span>
            </div>
            <div className="crwc-cell crwc-hthem" data-swap>
              <span style={{ color: 'var(--fg-2)', display: 'inline-flex' }}><CRWIcon d={target.icon} size={14} /></span>
              <span className="crwc-hthem-name">{target.name}</span>
              <span className="crwc-hthem-sub">{target.sub}</span>
            </div>
          </div>
          {ROWS.map((row, i) => (
            <div className={`crwc-r${i === ROWS.length - 1 ? ' crwc-r-last' : ''}`} key={row.label}>
              <div className="crwc-crit">
                <span className="crwc-crit-icon"><CRWIcon d={row.icon} size={13} /></span>
                {row.label}
              </div>
              <div className="crwc-cell crwc-us">
                <MarkIcon m="yes" us />
                <span className="crwc-txt">
                  <span className="crwc-t-full">{US[i]}</span>
                  <span className="crwc-t-short" aria-hidden="true">{US_M[i]}</span>
                </span>
              </div>
              <div className="crwc-cell crwc-them" data-swap>
                <MarkIcon m={target.cells[i][1]} />
                <span className="crwc-txt">
                  <span className="crwc-t-full">{target.cells[i][0]}</span>
                  <span className="crwc-t-short" aria-hidden="true">{target.cells[i][2] ?? target.cells[i][0]}</span>
                </span>
              </div>
            </div>
          ))}
          <div className="crwc-note" data-swap>
            <span className="crwc-note-label">Field note: {target.name.toLowerCase()}</span>
            {target.note}
          </div>
        </div>

        <p className="crwc-foot">Our reading of what each option offers, October 2026. Five working days applies after your first film.</p>
      </div>
    </section>
  );
}

/* ---------- THE ACCENT BAND: the first-film offer ---------- */
// The one full-bleed colour surface on the site: the break in an otherwise
// unbroken dark scroll, and the place to convert between the comparison and
// the price. The band is a lit horizon, and the light comes from the ruler.
// It is left-aligned on purpose: the sections above and below are centred.
//
// The ruler is a timeline ruler. One tick is one finished minute, which is the
// unit the price is quoted in, so the graphic states how the offer is counted.
const MINUTES = 40;

export function AccentCTA() {
  const ref = useRef<HTMLElement | null>(null);
  const [lit, setLit] = useState(false);

  // one-shot: the ruler lights as the band arrives, then stays lit
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setLit(true); return; }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { setLit(true); io.disconnect(); } },
      { rootMargin: '-12% 0px -12% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section className="crw-acta" ref={ref} aria-labelledby="crw-acta-h" data-lit={lit || undefined}>
      <div className="crw-acta-bloom" aria-hidden="true"></div>

      <div className="crw-acta-in">
        <span className="crw-acta-eyebrow">Your first film</span>
        <h2 id="crw-acta-h" className="crw-acta-h">Pay only if you publish it.</h2>
        <p className="crw-acta-sub">
          If the first film does not go on your channel, you do not pay for it.
        </p>

        {/* the minute ruler: majors every ten */}
        <div className="crw-acta-ruler" aria-hidden="true">
          {Array.from({ length: MINUTES }, (_, i) => (
            <i key={i} style={{ ['--i' as string]: String(i) }} data-major={i % 10 === 9 ? '' : undefined} />
          ))}
        </div>
        <div className="crw-acta-scale">
          <span className="crw-acta-scale-lead">Priced <b>per finished minute</b></span>
          <span className="crw-acta-scale-quiet"><CRWIcon d="check" size={13} /> Quoted before we start</span>
        </div>

        <div className="crw-acta-ctas">
          <a href="#contact" onClick={go('#contact')} style={{ textDecoration: 'none', display: 'flex' }}>
            {/* cream, not MetalBtn: the primary has to be the brightest object
                here, light you can press. */}
            <GradBtn size="lg" style={{ letterSpacing: '0.08em', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>Get a quote</GradBtn>
          </a>
          <a href="#films" onClick={go('#films')} className="crw-acta-link">
            <span className="crw-acta-link-icon"><CRWIcon d="arrowRight" size={11} /></span>
            Watch a finished film
          </a>
        </div>
      </div>
    </section>
  );
}

/* ---------- PRICING: one card, no price list ---------- */
const INCLUDED = [
  'Footage for every sentence',
  'Interview clips, with name cards',
  'AI-made scenes, approved by you',
  'Graphics and maps',
  'Sound design and music',
  'Subtitles',
  'The Premiere project and the master file',
];
const TERMS: [string, string][] = [
  ['First film', 'Pay only if you publish'],
  ['First film', 'About two weeks'],
  ['Every film after', 'Five working days'],
];

export function Pricing() {
  return (
    <section id="pricing" style={{ padding: '90px 24px 70px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <SectionHead center label="Pricing" title="One price per film." sub="Quoted before we start. Every layer is in it." />
        <div className="onq-card crw-glass crw-gradborder">
          <span className="onq-bloom" aria-hidden="true"></span>
          <div className="onq-left">
            <span className="cr-label">Per finished minute</span>
            <b className="onq-big crw-h-grad">About a third less than you pay your editor now.</b>
            <p className="onq-p">Tell us what a film costs you today. We send one quote for the whole film.</p>
            <a href="#contact" onClick={go('#contact')} style={{ textDecoration: 'none', display: 'inline-flex', marginTop: 6 }}>
              <MetalBtn size="lg">{CTA_LABEL}</MetalBtn>
            </a>
          </div>
          <div className="onq-right">
            <span className="cr-label">In every film</span>
            <ul className="onq-list">
              {INCLUDED.map((f) => (
                <li key={f}><span><CRWIcon d="check" size={11} sw={2.6} /></span>{f}</li>
              ))}
            </ul>
          </div>
          <div className="onq-terms">
            {TERMS.map(([k, v]) => (
              <div key={v}>
                <span>{k}</span>
                <b>{v}</b>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
